import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Palette, Type, Upload, AlertTriangle, CheckCircle2, Loader2, FileText } from "lucide-react";

const THEMES = [
  { id: "classic",    name: "Classic" },
  { id: "modern",     name: "Modern" },
  { id: "futuristic", name: "Futuristic" },
];
const LANGS = [
  { id: "en", label: "English" }, { id: "nl", label: "Nederlands" },
  { id: "es", label: "Español" },  { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" },  { id: "it", label: "Italiano" },
];
const FONTS = [
  { id: "system",    label: "System default (Helvetica)" },
  { id: "inter",     label: "Inter" },
  { id: "spacegrotesk", label: "Space Grotesk" },
  { id: "serif",     label: "Source Serif" },
];

const PALETTE_PRESETS = ["#00C566", "#0EA5E9", "#F97316", "#EF4444", "#8B5CF6", "#0A1628", "#0A0F0C"];

export default function SettingsReports() {
  const [ws, setWs] = useState(null);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspaces/mine");
      const w = Array.isArray(r.data) ? r.data[0] : (r.data?.workspaces?.[0] || r.data);
      setWs(w);
      if (w?.id) {
        const br = await api.get(`/workspaces/${w.id}/branding`);
        setBranding(br.data || {});
      }
    } catch (e) {
      setError("Could not load workspace branding");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    if (!ws?.id) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      const r = await api.patch(`/workspaces/${ws.id}/branding`, patch);
      setBranding(r.data?.branding || {});
      setSuccess("Saved.");
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { setError("Logo must be under 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => save({ logo_data_url: reader.result });
    reader.readAsDataURL(file);
  };

  if (loading) return <Layout title="Report branding"><div className="pm-card p-10 text-center text-slate-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div></Layout>;

  return (
    <Layout title="Report branding" subtitle="Agency branding applied to every generated report">
      <div className="max-w-3xl space-y-6" data-testid="settings-reports-page">
        {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-700 flex items-center gap-2"><AlertTriangle size={12} /> {error}</div>}
        {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12px] text-emerald-700 flex items-center gap-2"><CheckCircle2 size={12} /> {success}</div>}

        {/* Logo */}
        <div className="pm-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={14} className="text-emerald-600" />
            <h4 className="font-bold tracking-tight">Agency logo</h4>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
              {branding.logo_data_url ? (
                <img src={branding.logo_data_url} alt="Agency logo" className="max-w-full max-h-full object-contain" data-testid="branding-logo-preview" />
              ) : (
                <FileText size={24} className="text-slate-400" />
              )}
            </div>
            <div className="flex-1">
              <label className="pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 cursor-pointer" data-testid="branding-logo-upload">
                <Upload size={11} /> Upload logo
                <input type="file" accept="image/png,image/svg+xml,image/jpeg" onChange={handleLogoUpload} className="hidden" />
              </label>
              {branding.logo_data_url && (
                <button onClick={() => save({ logo_data_url: "" })} className="ml-2 pm-btn-secondary h-9 px-3 rounded-lg text-[12px] font-bold" data-testid="branding-logo-remove">
                  Remove
                </button>
              )}
              <div className="text-[10px] text-slate-500 mt-1.5">PNG, JPG or SVG · max 1 MB · transparent background recommended</div>
            </div>
          </div>
        </div>

        {/* Primary color */}
        <div className="pm-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Palette size={14} className="text-emerald-600" />
            <h4 className="font-bold tracking-tight">Primary accent colour</h4>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {PALETTE_PRESETS.map((c) => (
              <button key={c} onClick={() => save({ primary_color: c })}
                className={`w-10 h-10 rounded-xl border-2 transition-all ${branding.primary_color === c ? "border-slate-900 scale-110" : "border-slate-200 hover:scale-105"}`}
                style={{ background: c }} data-testid={`branding-color-${c.replace('#', '')}`} title={c} />
            ))}
            <div className="flex items-center gap-2 ml-3">
              <input type="color" value={branding.primary_color || "#00C566"}
                onChange={(e) => save({ primary_color: e.target.value })}
                className="w-10 h-10 rounded-xl cursor-pointer" data-testid="branding-color-picker" />
              <code className="text-[12px] text-slate-600">{branding.primary_color || "default"}</code>
            </div>
          </div>
        </div>

        {/* Default theme */}
        <div className="pm-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Type size={14} className="text-emerald-600" />
            <h4 className="font-bold tracking-tight">Default theme</h4>
          </div>
          <div className="flex items-center gap-2">
            {THEMES.map((t) => (
              <button key={t.id} onClick={() => save({ default_theme: t.id })}
                className={`px-3 py-1.5 rounded-lg border text-sm font-bold tracking-tight ${
                  (branding.default_theme || "classic") === t.id ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200"
                }`} data-testid={`branding-theme-${t.id}`}>{t.name}</button>
            ))}
          </div>
        </div>

        {/* Default language */}
        <div className="pm-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Type size={14} className="text-emerald-600" />
            <h4 className="font-bold tracking-tight">Default language</h4>
          </div>
          <select value={branding.default_language || "en"} onChange={(e) => save({ default_language: e.target.value })}
            className="pm-input text-[12px]" data-testid="branding-language">
            {LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </div>

        {/* Font */}
        <div className="pm-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Type size={14} className="text-emerald-600" />
            <h4 className="font-bold tracking-tight">Font family</h4>
          </div>
          <select value={branding.font_family || "system"} onChange={(e) => save({ font_family: e.target.value })}
            className="pm-input text-[12px]" data-testid="branding-font">
            {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <div className="text-[10px] text-slate-500 mt-1.5">Currently applied in the UI builder previews. The PDF still ships with Helvetica until custom font embedding lands in a later phase.</div>
        </div>

        {saving && <div className="text-[11px] text-slate-500 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Saving…</div>}
      </div>
    </Layout>
  );
}
